<template>
  <div class="crud-inscripciones">
    <!-- ── HEADER ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title-wrap">Inscripciones</h1>
        <p class="page-sub">
          Solicitudes recibidas desde la página pública · {{ total }} registros
        </p>
      </div>
    </div>

    <!-- ── BARRA DE FILTROS Y BÚSQUEDA ── -->
    <div class="filtros-bar">
      <!-- Búsqueda -->
      <div class="search-wrap">
        <i class="bi bi-search search-ico"></i>
        <input
          v-model="busqueda"
          class="search-input"
          placeholder="Buscar por nombre, apellido o email…"
          @input="onBusqueda"
        />
        <button v-if="busqueda" class="search-clear" @click="limpiarBusqueda">
          <i class="bi bi-x"></i>
        </button>
      </div>

      <!-- Filtro estatus -->
      <div class="tab-filtros">
        <button
          v-for="tab in tabsEstatus"
          :key="tab.value"
          :class="['tab-filt', { activo: filtroEstatus === tab.value }]"
          @click="seleccionarEstatus(tab.value)"
        >
          <span :class="['dot-estatus', tab.value || 'todos']"></span>
          {{ tab.label }}
          <span class="tab-count">{{ conteosPorEstatus[tab.value] ?? 0 }}</span>
        </button>
      </div>
    </div>

    <!-- ── TABLA ── -->
    <div class="tabla-wrapper">
      <div v-if="cargando" class="estado-centro">
        <span class="spinner-border spinner-border-sm text-warning me-2"></span>
        Cargando inscripciones…
      </div>

      <div v-else-if="!inscripcionesPagina.length" class="estado-centro">
        <i class="bi bi-inbox" style="font-size: 2rem; opacity: 0.25"></i>
        <p>Sin inscripciones para los filtros seleccionados.</p>
      </div>

      <table v-else class="tabla">
        <thead>
          <tr>
            <th @click="toggleSort('peleador_apellido')" class="th-sortable">
              Peleador <i :class="sortIconClass('peleador_apellido')"></i>
            </th>
            <th @click="toggleSort('peleador_peso_kg')" class="th-sortable">
              Peso <i :class="sortIconClass('peleador_peso_kg')"></i>
            </th>
            <th @click="toggleSort('peleador_cinta')" class="th-sortable">
              Cinta <i :class="sortIconClass('peleador_cinta')"></i>
            </th>
            <th @click="toggleSort('peleador_rol')" class="th-sortable">
              Rol <i :class="sortIconClass('peleador_rol')"></i>
            </th>
            <th @click="toggleSort('fecha_inscripcion')" class="th-sortable">
              Solicitud <i :class="sortIconClass('fecha_inscripcion')"></i>
            </th>
            <th @click="toggleSort('estatus')" class="th-sortable">
              Estatus <i :class="sortIconClass('estatus')"></i>
            </th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="ins in inscripcionesPagina" :key="ins.id" class="tabla-fila">
            <td>
              <div class="peleador-cell">
                <img
                  v-if="ins.peleador_foto"
                  :src="ins.peleador_foto"
                  class="avatar"
                  :alt="`${ins.peleador_nombre} ${ins.peleador_apellido}`"
                />
                <div v-else class="avatar-placeholder">
                  {{ ins.peleador_nombre?.charAt(0)
                  }}{{ ins.peleador_apellido?.charAt(0) }}
                </div>
                <div>
                  <p class="nombre">
                    {{ ins.peleador_nombre }} {{ ins.peleador_apellido }}
                  </p>
                  <p class="equipo">{{ ins.peleador_equipo }}</p>
                </div>
              </div>
            </td>
            <td>{{ ins.peleador_peso_kg }} kg</td>
            <td>
              <span :class="['badge-cinta', ins.peleador_cinta]">
                {{ ins.peleador_cinta_display }}
              </span>
            </td>
            <td>
              <span class="badge-rol">{{
                ins.peleador_rol_display || ins.peleador_rol
              }}</span>
            </td>
            <td class="fecha-cell">{{ formatFechaRelativa(ins.fecha_inscripcion) }}</td>
            <td>
              <span :class="['badge-estatus', ins.estatus]">{{
                labelEstatus(ins.estatus)
              }}</span>
            </td>
            <td>
              <div class="acciones-cell">
                <button class="btn-icon" title="Ver detalle" @click="abrirDetalle(ins)">
                  <i class="bi bi-eye-fill"></i>
                </button>
                <button
                  v-if="ins.estatus === 'pendiente'"
                  class="btn-icon btn-confirmar"
                  title="Confirmar"
                  @click="confirmarRapido(ins)"
                >
                  <i class="bi bi-check-lg"></i>
                </button>
                <button
                  v-if="ins.estatus === 'pendiente'"
                  class="btn-icon btn-rechazar"
                  title="Rechazar"
                  @click="abrirRechazo(ins)"
                >
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Paginación -->
      <div v-if="totalPaginas > 1" class="paginacion">
        <button class="pag-btn" :disabled="pagina === 1" @click="pagina--">
          <i class="bi bi-chevron-left"></i>
        </button>
        <template v-for="n in paginasVisibles" :key="n">
          <span v-if="n === '...'" class="pag-ellipsis">…</span>
          <button
            v-else
            :class="['pag-btn', { 'pag-activa': n === pagina }]"
            @click="pagina = n"
          >
            {{ n }}
          </button>
        </template>
        <button class="pag-btn" :disabled="pagina === totalPaginas" @click="pagina++">
          <i class="bi bi-chevron-right"></i>
        </button>
        <span class="pag-info">
          {{ (pagina - 1) * porPagina + 1 }}–{{
            Math.min(pagina * porPagina, inscripcionesOrdenadas.length)
          }}
          de {{ inscripcionesOrdenadas.length }}
        </span>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════
         MODAL — DETALLE DE INSCRIPCIÓN
    ════════════════════════════════════════════════════════════════════ -->
    <div v-if="modalDetalle" class="modal-overlay" @click.self="cerrarDetalle">
      <div class="modal-panel modal-detalle">
        <div class="modal-header">
          <div class="detalle-header-info" v-if="inscripcionActiva">
            <img
              v-if="inscripcionActiva.peleador_foto"
              :src="inscripcionActiva.peleador_foto"
              class="avatar avatar-lg"
            />
            <div v-else class="avatar-placeholder avatar-lg">
              {{ inscripcionActiva.peleador_nombre?.charAt(0)
              }}{{ inscripcionActiva.peleador_apellido?.charAt(0) }}
            </div>
            <div>
              <h2 class="modal-titulo">
                {{ inscripcionActiva.peleador_nombre }}
                {{ inscripcionActiva.peleador_apellido }}
              </h2>
              <p class="modal-sub">{{ inscripcionActiva.peleador_equipo }}</p>
              <span :class="['badge-estatus', inscripcionActiva.estatus]">
                {{ labelEstatus(inscripcionActiva.estatus) }}
              </span>
            </div>
          </div>
          <button class="btn-icon" @click="cerrarDetalle">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="detalle-body" v-if="inscripcionActiva">
          <!-- Fila de datos principales -->
          <div class="detalle-grid">
            <div class="detalle-item">
              <span class="detalle-label">Email</span>
              <span class="detalle-val">{{ inscripcionActiva.peleador_email }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Teléfono</span>
              <span class="detalle-val">{{ inscripcionActiva.peleador_telefono }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Género</span>
              <span class="detalle-val">{{
                inscripcionActiva.peleador_genero_display
              }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Nacionalidad</span>
              <span class="detalle-val">{{
                inscripcionActiva.peleador_nacionalidad_display
              }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Fecha de nacimiento</span>
              <span class="detalle-val">{{
                formatFechaLarga(inscripcionActiva.peleador_fecha_nacimiento)
              }}</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Peso</span>
              <span class="detalle-val">{{ inscripcionActiva.peleador_peso_kg }} kg</span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Cinta</span>
              <span :class="['badge-cinta', inscripcionActiva.peleador_cinta]">
                {{ inscripcionActiva.peleador_cinta_display }}
              </span>
            </div>
            <div class="detalle-item">
              <span class="detalle-label">Rol solicitado</span>
              <span class="badge-rol">{{
                inscripcionActiva.peleador_rol_display || inscripcionActiva.peleador_rol
              }}</span>
            </div>
            <div class="detalle-item col-span-2">
              <span class="detalle-label">Fecha de solicitud</span>
              <span class="detalle-val">{{
                formatFechaLarga(inscripcionActiva.fecha_inscripcion)
              }}</span>
            </div>

            <!-- Trayectoria -->
            <div
              class="detalle-item col-span-2"
              v-if="inscripcionActiva.peleador_trayectoria"
            >
              <span class="detalle-label">Trayectoria</span>
              <p class="detalle-texto">{{ inscripcionActiva.peleador_trayectoria }}</p>
            </div>

            <!-- Redes sociales -->
            <div
              class="detalle-item col-span-2"
              v-if="tieneRedes(inscripcionActiva.peleador_redes_sociales)"
            >
              <span class="detalle-label">Redes sociales</span>
              <div class="redes-row">
                <a
                  v-for="(url, red) in redesFiltradas(
                    inscripcionActiva.peleador_redes_sociales
                  )"
                  :key="red"
                  :href="url"
                  target="_blank"
                  rel="noopener noreferrer"
                  :class="['red-link', red]"
                >
                  <i :class="iconoRed(red)"></i>
                  <span>{{ red }}</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer con acciones si está pendiente -->
        <div class="modal-footer" v-if="inscripcionActiva">
          <button class="btn-secondary" @click="cerrarDetalle">Cerrar</button>
          <template v-if="inscripcionActiva.estatus === 'pendiente'">
            <button
              class="btn-rechazar-modal"
              :disabled="procesando"
              @click="abrirRechazo(inscripcionActiva)"
            >
              <i class="bi bi-x-circle-fill"></i> Rechazar
            </button>
            <button
              class="btn-confirmar-modal"
              :disabled="procesando"
              @click="confirmarRapido(inscripcionActiva)"
            >
              <span v-if="procesando">
                <span class="spinner-border spinner-border-sm"></span>
              </span>
              <span v-else><i class="bi bi-check-circle-fill"></i> Confirmar</span>
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════
         MODAL — RECHAZO CON MOTIVO
    ════════════════════════════════════════════════════════════════════ -->
    <div v-if="modalRechazo" class="modal-overlay" @click.self="cerrarRechazo">
      <div class="modal-panel modal-chico">
        <div class="modal-header">
          <div>
            <h2 class="modal-titulo modal-titulo-danger">
              <i class="bi bi-x-circle-fill"></i> Rechazar inscripción
            </h2>
            <p class="modal-sub" v-if="inscripcionRechazo">
              {{ inscripcionRechazo.peleador_nombre }}
              {{ inscripcionRechazo.peleador_apellido }}
            </p>
          </div>
          <button class="btn-icon" @click="cerrarRechazo">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="form-rechazo">
          <label class="label-form">
            Motivo del rechazo *
            <span class="char-count">{{ motivoRechazo.length }}/500</span>
          </label>
          <textarea
            v-model="motivoRechazo"
            class="input-form"
            rows="5"
            maxlength="500"
            placeholder="Explica el motivo del rechazo. Este mensaje se enviará por correo al peleador…"
          ></textarea>
          <p class="aviso-correo">
            <i class="bi bi-envelope-fill"></i>
            Se enviará un correo automático al peleador con este motivo.
          </p>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="cerrarRechazo" :disabled="procesando">
            Cancelar
          </button>
          <button
            class="btn-rechazar-modal"
            :disabled="procesando || !motivoRechazo.trim()"
            @click="ejecutarRechazo"
          >
            <span v-if="procesando">
              <span class="spinner-border spinner-border-sm"></span> Enviando…
            </span>
            <span v-else><i class="bi bi-x-circle-fill"></i> Confirmar rechazo</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import {
  getInscripcionesDashboard,
  cambiarEstatusInscripcion,
} from "@/utils/api_dashboard";

// ── Estado ────────────────────────────────────────────────────────────────────
const inscripciones = ref([]);
const cargando = ref(false);
const busqueda = ref("");
const filtroEstatus = ref("");
const procesando = ref(false);

// Ordenamiento
const sortCol = ref("fecha_inscripcion");
const sortDir = ref("desc");

// Paginación
const pagina = ref(1);
const porPagina = ref(12);

// Modales
const modalDetalle = ref(false);
const inscripcionActiva = ref(null);

const modalRechazo = ref(false);
const inscripcionRechazo = ref(null);
const motivoRechazo = ref("");

// ── Catálogos UI ─────────────────────────────────────────────────────────────
const tabsEstatus = [
  { value: "", label: "Todas" },
  { value: "pendiente", label: "Pendientes" },
  { value: "confirmado", label: "Confirmadas" },
  { value: "rechazado", label: "Rechazadas" },
];

// ── Carga ─────────────────────────────────────────────────────────────────────
async function cargar() {
  pagina.value = 1;
  cargando.value = true;
  try {
    const params = {};
    if (filtroEstatus.value) params.estatus = filtroEstatus.value;
    if (busqueda.value.trim()) params.q = busqueda.value.trim();
    inscripciones.value = await getInscripcionesDashboard(params);
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudieron cargar las inscripciones.",
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    cargando.value = false;
  }
}

// Debounce para búsqueda de texto
let busquedaTimer = null;
function onBusqueda() {
  clearTimeout(busquedaTimer);
  busquedaTimer = setTimeout(cargar, 350);
}

function limpiarBusqueda() {
  busqueda.value = "";
  cargar();
}

function seleccionarEstatus(val) {
  filtroEstatus.value = val;
  cargar();
}

onMounted(cargar);

// ── Totales y conteos ─────────────────────────────────────────────────────────
const total = computed(() => inscripciones.value.length);

const conteosPorEstatus = computed(() => {
  const counts = { "": inscripciones.value.length };
  for (const ins of inscripciones.value) {
    counts[ins.estatus] = (counts[ins.estatus] || 0) + 1;
  }
  return counts;
});

// ── Ordenamiento ─────────────────────────────────────────────────────────────
function toggleSort(col) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === "asc" ? "desc" : "asc";
  } else {
    sortCol.value = col;
    sortDir.value = col === "fecha_inscripcion" ? "desc" : "asc";
  }
  pagina.value = 1;
}

function sortIconClass(col) {
  if (sortCol.value !== col) return "bi bi-arrow-down-up sort-neutral";
  return sortDir.value === "asc"
    ? "bi bi-arrow-up sort-active"
    : "bi bi-arrow-down sort-active";
}

const inscripcionesOrdenadas = computed(() => {
  const col = sortCol.value;
  const dir = sortDir.value === "asc" ? 1 : -1;
  return [...inscripciones.value].sort((a, b) => {
    const va = (a[col] ?? "").toString().toLowerCase();
    const vb = (b[col] ?? "").toString().toLowerCase();
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });
});

// ── Paginación ────────────────────────────────────────────────────────────────
const totalPaginas = computed(() =>
  Math.max(1, Math.ceil(inscripcionesOrdenadas.value.length / porPagina.value))
);

const inscripcionesPagina = computed(() => {
  const inicio = (pagina.value - 1) * porPagina.value;
  return inscripcionesOrdenadas.value.slice(inicio, inicio + porPagina.value);
});

const paginasVisibles = computed(() => {
  const total = totalPaginas.value;
  const cur = pagina.value;
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (cur > 3) pages.push("...");
  for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++)
    pages.push(i);
  if (cur < total - 2) pages.push("...");
  pages.push(total);
  return pages;
});

// ── Modales ───────────────────────────────────────────────────────────────────
function abrirDetalle(ins) {
  inscripcionActiva.value = ins;
  modalDetalle.value = true;
}

function cerrarDetalle() {
  modalDetalle.value = false;
  inscripcionActiva.value = null;
}

function abrirRechazo(ins) {
  inscripcionRechazo.value = ins;
  motivoRechazo.value = "";
  modalRechazo.value = true;
  // Cierra el modal de detalle si estaba abierto
  modalDetalle.value = false;
}

function cerrarRechazo() {
  modalRechazo.value = false;
  inscripcionRechazo.value = null;
  motivoRechazo.value = "";
}

// ── Acciones ──────────────────────────────────────────────────────────────────
async function confirmarRapido(ins) {
  const confirm = await Swal.fire({
    title: "¿Confirmar inscripción?",
    text: `${ins.peleador_nombre} ${ins.peleador_apellido} recibirá un correo de confirmación.`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Sí, confirmar",
    cancelButtonText: "Cancelar",
    background: "#0f1520",
    color: "#fff",
    confirmButtonColor: "#f3bc2c",
    cancelButtonColor: "transparent",
  });
  if (!confirm.isConfirmed) return;

  procesando.value = true;
  try {
    const actualizada = await cambiarEstatusInscripcion(ins.id, {
      estatus: "confirmado",
    });
    // Actualizar en la lista local sin re-fetch
    const idx = inscripciones.value.findIndex((i) => i.id === ins.id);
    if (idx !== -1) inscripciones.value[idx] = actualizada;

    // Si el modal de detalle está abierto, refrescar
    if (inscripcionActiva.value?.id === ins.id) {
      inscripcionActiva.value = actualizada;
    }

    Swal.fire({
      icon: "success",
      title: "Inscripción confirmada",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
      background: "#0f1520",
      color: "#fff",
    });
  } catch (e) {
    const msg = e.response?.data
      ? JSON.stringify(e.response.data)
      : "Error al confirmar.";
    Swal.fire({
      icon: "error",
      title: "Error",
      text: msg,
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    procesando.value = false;
  }
}

async function ejecutarRechazo() {
  if (!motivoRechazo.value.trim() || !inscripcionRechazo.value) return;
  procesando.value = true;
  try {
    const actualizada = await cambiarEstatusInscripcion(inscripcionRechazo.value.id, {
      estatus: "rechazado",
      motivo_rechazo: motivoRechazo.value.trim(),
    });

    const idx = inscripciones.value.findIndex(
      (i) => i.id === inscripcionRechazo.value.id
    );
    if (idx !== -1) inscripciones.value[idx] = actualizada;

    cerrarRechazo();
    Swal.fire({
      icon: "success",
      title: "Inscripción rechazada",
      text: "Se envió el correo con el motivo al peleador.",
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      background: "#0f1520",
      color: "#fff",
    });
  } catch (e) {
    const msg = e.response?.data ? JSON.stringify(e.response.data) : "Error al rechazar.";
    Swal.fire({
      icon: "error",
      title: "Error",
      text: msg,
      background: "#0f1520",
      color: "#fff",
      confirmButtonColor: "#f3bc2c",
    });
  } finally {
    procesando.value = false;
  }
}

// ── Helpers de UI ─────────────────────────────────────────────────────────────
function labelEstatus(est) {
  const mapa = {
    confirmado: "Confirmado",
    pendiente: "Pendiente",
    rechazado: "Rechazado",
  };
  return mapa[est] ?? est ?? "—";
}

function formatFechaRelativa(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "Hace un momento";
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  const dias = Math.floor(diff / 86400);
  if (dias < 7) return `Hace ${dias} día${dias > 1 ? "s" : ""}`;
  return formatFechaLarga(iso);
}

function formatFechaLarga(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const meses = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function tieneRedes(redes) {
  return redes && Object.values(redes).some((v) => !!v);
}

function redesFiltradas(redes) {
  if (!redes) return {};
  return Object.fromEntries(Object.entries(redes).filter(([, v]) => !!v));
}

function iconoRed(red) {
  const mapa = {
    instagram: "bi bi-instagram",
    facebook: "bi bi-facebook",
    youtube: "bi bi-youtube",
    twitter: "bi bi-twitter-x",
    tiktok: "bi bi-tiktok",
  };
  return mapa[red] ?? "bi bi-link-45deg";
}
</script>

<style scoped>
.crud-inscripciones {
  padding: 32px;
  font-family: "Inter", sans-serif;
  color: #fff;
  min-height: 100%;
}

/* ── Header ── */
.page-header {
  margin-bottom: 20px;
}
.page-title-wrap {
  color: #f3bc2c;
  font-size: 22px;
  font-weight: 700;
  margin: 0;
}
.page-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  margin: 4px 0 0;
}

/* ── Barra filtros ── */
.filtros-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

/* Buscador */
.search-wrap {
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 340px;
}
.search-ico {
  position: absolute;
  left: 11px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  pointer-events: none;
}
.search-input {
  width: 100%;
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #fff;
  padding: 8px 32px 8px 32px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.search-input:focus {
  border-color: rgba(243, 188, 44, 0.4);
}
.search-input::placeholder {
  color: rgba(255, 255, 255, 0.25);
}
.search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.35);
  cursor: pointer;
  font-size: 14px;
  padding: 2px;
  transition: color 0.15s;
}
.search-clear:hover {
  color: #fff;
}

/* Tabs de estatus */
.tab-filtros {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}
.tab-filt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.5);
  padding: 7px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.tab-filt:hover {
  border-color: rgba(255, 255, 255, 0.3);
  color: #fff;
}
.tab-filt.activo {
  border-color: #f3bc2c;
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.08);
}
.tab-count {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 600;
}
.tab-filt.activo .tab-count {
  background: rgba(243, 188, 44, 0.15);
}

.dot-estatus {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot-estatus.todos {
  background: rgba(255, 255, 255, 0.4);
}
.dot-estatus.pendiente {
  background: #facc15;
}
.dot-estatus.confirmado {
  background: #4ade80;
}
.dot-estatus.rechazado {
  background: #f87171;
}

/* ── Tabla ── */
.tabla-wrapper {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  /* ✅ Responsive: scroll horizontal en vez de recortar contenido. */
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.estado-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}
.tabla {
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  font-size: 13px;
}
.tabla thead tr {
  background: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.tabla th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
}
.th-sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s;
}
.th-sortable:hover {
  color: #f3bc2c;
}
.sort-neutral {
  opacity: 0.3;
  font-size: 10px;
  margin-left: 4px;
}
.sort-active {
  color: #f3bc2c;
  font-size: 10px;
  margin-left: 4px;
}

.tabla-fila {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background 0.15s;
}
.tabla-fila:hover {
  background: rgba(255, 255, 255, 0.03);
}
.tabla td {
  padding: 12px 16px;
  vertical-align: middle;
}

.peleador-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}
.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(243, 188, 44, 0.18);
  color: #f3bc2c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.avatar-lg {
  width: 52px !important;
  height: 52px !important;
  font-size: 15px !important;
}
.nombre {
  font-weight: 600;
  margin: 0;
  font-size: 13px;
}
.equipo {
  margin: 0;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}
.fecha-cell {
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  white-space: nowrap;
}

/* Acciones en fila */
.acciones-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Cinta badges */
.badge-cinta {
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid transparent;
}
.badge-cinta.blanca {
  background: rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  border-color: rgba(255, 255, 255, 0.2);
}
.badge-cinta.blanca_amarilla {
  background: rgba(254, 240, 138, 0.12);
  color: #fde047;
  border-color: rgba(250, 204, 21, 0.3);
}
.badge-cinta.amarilla {
  background: rgba(250, 204, 21, 0.15);
  color: #facc15;
  border-color: rgba(250, 204, 21, 0.3);
}
.badge-cinta.naranja {
  background: rgba(251, 146, 60, 0.15);
  color: #fb923c;
  border-color: rgba(251, 146, 60, 0.3);
}
.badge-cinta.verde {
  background: rgba(34, 197, 94, 0.12);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.25);
}
.badge-cinta.azul {
  background: rgba(59, 130, 246, 0.15);
  color: #60a5fa;
  border-color: rgba(59, 130, 246, 0.3);
}
.badge-cinta.morada {
  background: rgba(168, 85, 247, 0.15);
  color: #c084fc;
  border-color: rgba(168, 85, 247, 0.3);
}
.badge-cinta.cafe {
  background: rgba(180, 83, 9, 0.18);
  color: #d97706;
  border-color: rgba(217, 119, 6, 0.3);
}
.badge-cinta.negra {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.55);
  border-color: rgba(255, 255, 255, 0.12);
}

.badge-rol {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 2px 10px;
  font-size: 11px;
  white-space: nowrap;
}
.badge-estatus {
  border-radius: 20px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.badge-estatus.confirmado {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
}
.badge-estatus.pendiente {
  background: rgba(234, 179, 8, 0.15);
  color: #facc15;
}
.badge-estatus.rechazado {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

/* ── Paginación ── */
.paginacion {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 14px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  flex-wrap: wrap;
}
.pag-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pag-btn:hover:not(:disabled) {
  border-color: #f3bc2c;
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.08);
}
.pag-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
.pag-activa {
  background: #f3bc2c !important;
  color: #0f172a !important;
  border-color: #f3bc2c !important;
  font-weight: 700;
}
.pag-ellipsis {
  color: rgba(255, 255, 255, 0.3);
  font-size: 13px;
  padding: 0 4px;
}
.pag-info {
  margin-left: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
}

/* ── Botones ── */
.btn-icon {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.15s;
}
.btn-icon:hover {
  color: #f3bc2c;
  background: rgba(243, 188, 44, 0.1);
}
.btn-confirmar:hover {
  color: #4ade80 !important;
  background: rgba(34, 197, 94, 0.1) !important;
}
.btn-rechazar:hover {
  color: #f87171 !important;
  background: rgba(239, 68, 68, 0.1) !important;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-secondary:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.05);
}
.btn-secondary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-confirmar-modal {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-confirmar-modal:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.25);
  border-color: #4ade80;
}
.btn-confirmar-modal:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.btn-rechazar-modal {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(239, 68, 68, 0.12);
  color: #f87171;
  border: 1px solid rgba(248, 113, 113, 0.25);
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-rechazar-modal:hover:not(:disabled) {
  background: rgba(239, 68, 68, 0.22);
  border-color: #f87171;
}
.btn-rechazar-modal:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.modal-panel {
  background: #0f1520;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  width: 100%;
}
.modal-detalle {
  max-width: 640px;
}
.modal-chico {
  max-width: 500px;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 22px 24px 16px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.detalle-header-info {
  display: flex;
  align-items: center;
  gap: 14px;
}
.modal-titulo {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  color: #f3bc2c;
}
.modal-titulo-danger {
  color: #f87171 !important;
}
.modal-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 4px 0 6px;
}

/* Cuerpo detalle */
.detalle-body {
  overflow-y: auto;
  flex: 1;
  padding: 20px 24px;
}
.detalle-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
}
.detalle-item {
  padding: 11px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.detalle-item.col-span-2 {
  grid-column: span 2;
}
.detalle-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255, 255, 255, 0.35);
}
.detalle-val {
  font-size: 13px;
  color: #fff;
}
.detalle-texto {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0;
}

/* Redes */
.redes-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 2px;
}
.red-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  text-transform: capitalize;
  transition: all 0.15s;
}
.red-link:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}
.red-link.instagram:hover {
  border-color: #e1306c;
  color: #e1306c;
}
.red-link.facebook:hover {
  border-color: #1877f2;
  color: #60a5fa;
}
.red-link.youtube:hover {
  border-color: #ff0000;
  color: #f87171;
}
.red-link.twitter:hover {
  border-color: #fff;
  color: #fff;
}

/* Form rechazo */
.form-rechazo {
  padding: 20px 24px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.label-form {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.45);
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.char-count {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  font-weight: 400;
  text-transform: none;
}
.input-form {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #fff;
  padding: 10px 12px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
  font-family: "Inter", sans-serif;
  resize: vertical;
  box-sizing: border-box;
  width: 100%;
}
.input-form:focus {
  border-color: rgba(243, 188, 44, 0.5);
}
.aviso-correo {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  margin-top: 4px;
}
.aviso-correo i {
  color: #f3bc2c;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  flex-shrink: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  flex-wrap: wrap;
}

/* ── Responsive ── */
@media (max-width: 640px) {
  .crud-inscripciones {
    padding: 16px;
  }
  .filtros-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .filtros-bar .search-wrap {
    max-width: none;
  }
  .modal-overlay {
    padding: 12px;
  }
}
</style>
